<template>
  <div class="threads">
    <div v-if="medicalCases === null" class="loading">
      <div class="spinner spinner--running"></div>
    </div>

    <div v-else-if="medicalCases.length === 0" class="threads__empty">
      <img src="@/assets/svg/folder-open.svg" class="threads__empty-icon" alt="E-mail" />
      <strong class="threads__empty-title">Brak zgłoszeń</strong>
      <p class="threads__empty-desc">Tu pojawi się lista wszystkich, dostępnych dla Ciebie zgłoszeń.</p>
    </div>

    <div v-else>
      <div class="threads__header">
        <div>
          <div class="threads__title">Lista zgłoszeń</div>
          <div class="threads__filters">
            <span
              >Aktywne filtry:
              {{ 0 + (filters.state ? 1 : 0) + (filters.status ? 1 : 0) + (filters.urgent ? 1 : 0) }}</span
            >
            <button
              @click="filters = { state: '', status: '', urgent: '' }; showFilters = false"
              v-if="filters.state || filters.status || filters.urgent"
              class="threads__filters-clear"
              title="Wyczyść filtry"
            >
              <IconTrash class="threads__filters-icon" />
            </button>
          </div>
        </div>

        <button @click="showFilters = !showFilters" class="threads__filters-toggle">
          <IconListFilter />
          Filtruj listę
        </button>
      </div>

      <div class="filters" v-show="showFilters">
        <div class="filters__group">
          <p class="filters__group-title">Typ zgłoszenia</p>
          <div class="filters__options">
            <button
              @click="updateFilter('state', 'new')"
              :class="{ filters__option: true, 'filters__option--active': filters.state === 'new' }"
            >
              Nowe
            </button>
            <button
              @click="updateFilter('state', 'acknowledged')"
              :class="{ filters__option: true, 'filters__option--active': filters.state === 'acknowledged' }"
            >
              Przyjęte
            </button>
            <button
              @click="updateFilter('state', 'ongoing')"
              :class="{ filters__option: true, 'filters__option--active': filters.state === 'ongoing' }"
            >
              W toku
            </button>
            <button
              @click="updateFilter('state', 'resolved')"
              :class="{ filters__option: true, 'filters__option--active': filters.state === 'resolved' }"
            >
              Rozwiązane
            </button>
          </div>
        </div>
        <div class="filters__group">
          <p class="filters__group-title">Aktywność</p>
          <div class="filters__options">
            <button
              @click="updateFilter('status', 'new_reply')"
              :class="{ filters__option: true, 'filters__option--active': filters.status === 'new_reply' }"
            >
              Nowa wiad.
            </button>
            <button
              @click="updateFilter('status', 'readed')"
              :class="{ filters__option: true, 'filters__option--active': filters.status === 'readed' }"
            >
              Przeczytane
            </button>
          </div>
        </div>
        <div class="filters__group">
          <p class="filters__group-title">Ważność</p>
          <div class="filters__options">
            <button
              @click="updateFilter('urgent', 'urgent')"
              :class="{ filters__option: true, 'filters__option--active': filters.urgent === 'urgent' }"
            >
              Priorytetowe
            </button>
            <button
              @click="updateFilter('urgent', 'non_urgent')"
              :class="{ filters__option: true, 'filters__option--active': filters.urgent === 'non_urgent' }"
            >
              Niepriorytetowe
            </button>
          </div>
        </div>

        * Kliknij ponownie na filtr aby go wyłączyć
      </div>

      <div class="threads__list" ref="threadList">
        <div v-for="medCase in filteredMedicalCases()" :key="medCase.id" @click="chooseCase(medCase.id)">
          <ThreadListItem :med-case="medCase" :current-report="currentReportId" />
        </div>
        <div v-if="filteredMedicalCases().length === 0" class="threads__empty">
          <img src="@/assets/svg/folder-open.svg" class="threads__empty-icon" alt="E-mail" />
          <strong class="threads__empty-title">Brak zgłoszeń</strong>
          <p class="threads__empty-desc">Brak zgłoszeń spełniających wybrane kryteria.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { IconListFilter, IconTrash } from "@/components/ui/icons";

import ThreadListItem from "@/components/messages/threads/ThreadListItem";

export default {
  name: "ThreadList",
  data() {
    return {
      currentReportId: "",
      latestOpenedId: "",
      showFilters: false,
      scrolledToLatest: false,
      filters: {
        state: "", // new, acknowledged, ongoing, resolved
        status: "", // new_reply, new_file
        urgent: "", // urgent
      },
      sort: {
        by: "created_at",
        order: "desc",
      },
    };
  },
  props: {
    currentReport: {
      type: String,
    },
    latestOpened: {
      type: String,
    },
    medicalCases: {
      type: Array,
      required: true,
    },
  },
  watch: {
    medicalCases() {
      if (this.medicalCases.length > 0 && !this.scrolledToLatest) {
        this.scrollToLatestOpened()
        this.scrolledToLatest = true;
      }
    },
  },
  components: {
    ThreadListItem,
    IconListFilter,
    IconTrash,
  },
  computed: mapGetters({
    token: "getToken",
    userData: "getUserData",
    headers: "getSecuredRequestHeader",
  }),
  methods: {
    filteredMedicalCases() {
      let cases = this.medicalCases;

      if (this.filters.state) {
        cases = cases.filter((medCase) => medCase.state === this.filters.state);
      }

      if (this.filters.status) {
        cases = cases.filter((medCase) => {
          if (this.filters.status === "readed") return medCase.read.you === true;
          if (this.filters.status === "new_reply") return medCase.read.you === false;
          return false;
        });
      }

      if (this.filters.urgent) {
        cases = cases.filter((medCase) => {
          if (this.filters.urgent === "urgent") return !!medCase.urgent;
          if (this.filters.urgent === "non_urgent") return !medCase.urgent;
          return false;
        });
      }

      return cases;
    },
    chooseCase(caseId) {
      this.currentReportId = caseId;
      this.$emit("loadCase", caseId);
    },
    scrollToLatestOpened() {
      if (!this.latestOpenedId) return;
      this.$nextTick(() => {
        const latestOpened = this.$refs.threadList.querySelector(`[data-id="${this.latestOpenedId}"]`);
        if (!latestOpened) return;
        latestOpened.scrollIntoView({ behavior: "smooth" });
        this.chooseCase(this.latestOpenedId);
      });
    },
    updateFilter(filter, value) {
      this.filters[filter] && this.filters[filter] === value
        ? (this.filters[filter] = "")
        : (this.filters[filter] = value);
    },
  },
  beforeMount() {
    if (this.currentReport) {
      this.currentReportId = this.currentReport;
    }
    if (this.latestOpened) {
      this.latestOpenedId = this.latestOpened;
    }
  },
};
</script>

<style lang="scss">
.threads {
  flex: 2;
  max-width: 340px;
  box-shadow: inset -10px 0px 10px -10px rgba(0, 0, 0, 0.16);
  background: #f7f7f7;
  max-height: calc(100vh - 70px);
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 80px;
    border-bottom: 2px solid #e1e1e1;
    padding: 10px 15px;
    box-sizing: border-box;
  }
  &__title {
    font-size: 14px;
    font-weight: bold;
    color: rgb(73, 73, 73);
    margin-bottom: 3px;
  }
  &__filters {
    font-size: 14px;
    color: #7e7e7e;
    display: flex;
    align-items: center;
    gap: 1rem;

    &-clear {
      width: 28px;
      height: 28px;
      padding: 4px;
      margin: -4px 0;
      background: #fde8e8;
      color: #da3737;
      border-radius: 20px;
      aspect-ratio: 1;
      border: none;
      cursor: pointer;

      &:hover {
        background: #f5cfcf;
        color: #8d0505;
      }
    }

    &-icon {
      width: 16px;
      height: 16px;
    }

    &-toggle {
      font-size: 14px;
      padding: 8px 16px;
      border: 1px solid #e7e7e7;
      background: #fff;
      border-radius: 20px;
      cursor: pointer;
      transition: all 150ms;

      &:hover {
        background: #e1e1e1;
      }
    }
  }
  &__list {
    height: calc(100vh - 150px);
    overflow: auto;
    &::-webkit-scrollbar {
      width: 5px;
      height: 5px;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    padding: 32px;
    margin: 32px;
    border: 2px dashed #e0e0e0;
    border-radius: 30px;
    text-align: center;

    &-icon {
      width: 64px;
      height: 64px;
      margin: auto;
      padding: 8px;
    }

    &-title {
      margin: 8px;
    }

    &-desc {
      margin: 0;
      font-size: 14px;
    }
  }
}

.filters {
  padding: 16px;
  border-bottom: 2px solid #e1e1e1;
  font-size: 14px;

  &__group {
    margin: 0 0 16px 0;
  }

  &__group-title {
    font-size: 14px;
    font-weight: bold;
    margin: 4px 0;
    color: gray;
  }

  &__options {
    display: flex;
    width: 100%;
    margin: 4px 0;
  }

  &__option {
    flex: 1;
    font-size: 14px;
    padding: 6px 8px;
    white-space: nowrap;
    text-align: center;
    cursor: pointer;
    background: #fff;
    border: 2px solid #e7e7e7;
    transition: all 150ms;

    &:first-of-type {
      border-radius: 20px 0 0 20px;
    }

    &:last-of-type {
      border-radius: 0 20px 20px 0;
    }

    &:not(:last-of-type) {
      margin-right: -2px;
    }

    &:hover {
      background: #e1e1e1;
      color: black;
    }

    &--active {
      --active-color: #aeca6a;
      color: #fff;
      background: var(--active-color);
      border-color: var(--active-color);
      position: relative;
    }
  }
}
</style>
